import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { SearchResultCardLayout } from '../SearchResultCardLayout/SearchResultCardLayout'

import { Text } from '@percent/lemonade'
import type { SearchOpportunitiesResponseRow } from '@percent/workplace-giving/api/search/searchOpportunities/searchOpportunities.types'
import { OpportunityLocationTypeDto, OpportunityType } from '@percent/workplace-giving/api/opportunity/dto'
import { formatShortOfflineOpportunityAddress } from '@percent/workplace-giving/utils/formatOfflineOpportunityAddress'
import { useDateTimeFmt } from '@percent/workplace-giving/common/hooks/useDateTimeFmt/useDateTimeFmt'
import classes from './OpportunityCard.module.scss'
import fallbackLogo from '@percent/workplace-giving/assets/images/fallback-logo.svg'
import { useOrganisationBadge } from '@percent/workplace-giving/common/hooks'

export type OpportunityCardProps = Readonly<{
  opportunity: SearchOpportunitiesResponseRow
  track?: VoidFunction
  linkAdditionalParams?: URLSearchParams
}>

export function OpportunityCard({ opportunity, track, linkAdditionalParams }: OpportunityCardProps): JSX.Element {
  const { t } = useTranslation()
  const { formatDateWODay } = useDateTimeFmt()
  const { badge, badgeTooltip } = useOrganisationBadge(opportunity.organisation?.id)

  const locationText = useMemo(() => {
    if (opportunity.type === OpportunityType.EventSeries || opportunity.isSeries)
      return t('workplace_giving.volunteering.multipleSessions')

    if (opportunity.location.type === OpportunityLocationTypeDto.Virtual)
      return t('workplace_giving.volunteering.virtualOpportunity')

    return formatShortOfflineOpportunityAddress({ location: opportunity.location })
  }, [opportunity.type, opportunity.location, opportunity.isSeries, t])

  const linkTo = useMemo(() => {
    const basePath = `/volunteer/opportunities/${opportunity.id}`
    const queryString = linkAdditionalParams ? `?${linkAdditionalParams.toString()}` : ''

    return `${basePath}${queryString}`
  }, [opportunity.id, linkAdditionalParams])

  return (
    <SearchResultCardLayout
      title={opportunity.name}
      linkTo={linkTo}
      handleTracking={() => track?.()}
      image={opportunity.image}
      logo={opportunity.organisation ? opportunity.organisation?.logo || fallbackLogo : undefined}
      logoTooltipText={opportunity.organisation?.name}
      logoBadge={badge}
      logoBadgeTooltip={badgeTooltip}
    >
      <div className={classes.Details}>
        <Text as="p" size="small">
          {!opportunity.startDate ||
          (opportunity.timeTracking === 'manual' &&
            opportunity.endDate &&
            new Date(opportunity.startDate).toDateString() !== new Date(opportunity.endDate).toDateString())
            ? null
            : `${formatDateWODay(new Date(opportunity.startDate))} · `}
          {locationText}
        </Text>
      </div>
    </SearchResultCardLayout>
  )
}
